import { EventEmitter } from 'events';
import { fromEventPattern, from, of, merge, timer, race, iif, defer } from 'rxjs';
import { timeout, take, catchError, map, distinctUntilChanged, filter, switchMap, defaultIfEmpty, withLatestFrom, mergeMap, scan, shareReplay, bufferToggle, throttleTime, tap, retryWhen, delay, } from 'rxjs/operators';
import lodashGet from 'lodash/get';
import { TEAM_EVENT_NAME } from '@/api/logical/im_team';
const switchTeamCustomName = param => {
    if (param.name.indexOf('LINGXI_IM_TEAM_DEFAULT_NAME') === -1) {
        param.customTeamName = param.name;
        return param;
    }
    const { serverCustom } = param;
    let customTeamName = param.name;
    try {
        const result = JSON.parse(serverCustom || '');
        customTeamName = result.auto_team_name;
    }
    catch (ex) {
        console.warn('[im] ', ex);
    }
    param.customTeamName = customTeamName;
    return param;
};
export class ImTeamStream {
    constructor() {
        this.sdk = null;
        this.eventEmitter = new EventEmitter();
        this.contactEventEmitter = new EventEmitter();
        const onSyncTeams = (handler) => {
            this.eventEmitter.addListener(ImTeamStream.Event_Names.onSyncTeams, handler);
        };
        const offSyncTeams = (handler) => {
            this.eventEmitter.removeListener(ImTeamStream.Event_Names.onSyncTeams, handler);
        };
        const $stream = fromEventPattern(onSyncTeams, offSyncTeams);
        this.observable = $stream.pipe(map(teams => {
            if (Array.isArray(teams)) {
                return teams.map(item => switchTeamCustomName(item));
            }
            return switchTeamCustomName(teams);
        }), scan((total, teams) => {
            if (Array.isArray(teams)) {
                const teamMaps = teams.reduce((total, team) => (Object.assign(Object.assign({}, total), { [team.teamId]: team })), {});
                return Object.assign(Object.assign({}, total), teamMaps);
            }
            return Object.assign(Object.assign({}, total), { [teams.teamId]: teams });
        }, {}), shareReplay(1));
    }
    init(sdk) {
        this.sdk = sdk;
        const $teams = this._onteams();
        this.syncContact();
        this.syncIM();
        $teams.subscribe(teams => {
            this.eventEmitter.emit(ImTeamStream.Event_Names.onSyncTeams, teams);
        });
    }
    syncIM() {
        const $updateTeam = this._onupdateteam();
        const $addTeamMember = this._onAddTeamMember();
        const $removeTeamMember = this._onRemoveTeamMember();
        const $transferTeam = this._onTransferTeam();
        const $dismissTeam = this._onDissmissTeam();
        const $teamEvent = merge($updateTeam, $addTeamMember.pipe(map(param => param.team)), $removeTeamMember.pipe(map(param => param.team)), $transferTeam.pipe(map(param => param.team)), $dismissTeam);
        const $teamEventBuffer = $teamEvent.pipe(map(team => team.teamId), bufferToggle($teamEvent.pipe(throttleTime(100)), () => timer(100)));
        const onRequstTeam = (handler) => {
            this.eventEmitter.addListener(ImTeamStream.Event_Names.request, handler);
        };
        const offRequestTeam = (handler) => {
            this.eventEmitter.removeListener(ImTeamStream.Event_Names.request, handler);
        };
        const $requestEvent = fromEventPattern(onRequstTeam, offRequestTeam);
        $requestEvent.subscribe(teamId => {
            console.log('[im.team]request', teamId);
        });
        const $requestEventBuffer = $requestEvent.pipe(tap(({ teamId }) => {
            console.log('[im.team]request', teamId);
        }), filter(({ teamId }) => /^\d+$/.test(teamId)), withLatestFrom(this.observable), filter(([teamUpdateInfo, teamMap]) => {
            const { teamId, isForceUpdate } = teamUpdateInfo;
            if (isForceUpdate) {
                return true;
            }
            const tids = Object.keys(teamMap);
            return Object.keys(teamMap).length > 0 && !tids.includes(teamId);
        }), map(([teamUpdateInfo]) => teamUpdateInfo.teamId), bufferToggle($requestEvent.pipe(throttleTime(50)), () => timer(50)), filter(teamIds => teamIds.length > 0));
        merge($teamEventBuffer, $requestEventBuffer)
            .pipe(map(_ids => [...new Set(_ids)].filter(_id => /^[\d]+$/.test(_id))), filter(tids => tids.length > 0), mergeMap(teamIds => {
            const $request = defer(() => {
                const _request = this.sdk.excute('getTeamsById', {
                    teamIds,
                });
                return from(_request);
            });
            return $request.pipe(retryWhen($error => $error.pipe(tap(err => {
                console.log('[im.team]batchrequest.failed', err);
            }), delay(1000), take(10))));
        }), map(({ teams, tids }) => [
            ...teams,
            ...tids.map(tid => ({
                teamId: tid,
                valid: false,
                name: '未知群组',
            })),
        ]), catchError(() => from(Promise.resolve([]))))
            .subscribe(teams => {
            this.eventEmitter.emit(ImTeamStream.Event_Names.onSyncTeams, teams);
        });
    }
    syncContact() {
        const $myinfo = this.sdk.imself.getSubject();
        const $updateTeam = this._onupdateteam();
        const $addTeamMember = this._onAddTeamMember();
        const $removeTeamMember = this._onRemoveTeamMember();
        const $transferTeam = this._onTransferTeam();
        const $dismissTeam = this._onDissmissTeam();
        const $contactUpdateTeam = $updateTeam.pipe(map(teamInfo => ({
            action: TEAM_EVENT_NAME.UPDATE_TEAM,
            team: teamInfo,
        })));
        const $contactTransferTeam = $transferTeam.pipe(map(param => ({
            action: TEAM_EVENT_NAME.TRANSFER_TEAM,
            team: param.team,
            accounts: [param.from.id, param.to.id].map(item => item.replace(/^\d+-/, '')),
        })));
        const $contactLeaveTeam = merge($dismissTeam.pipe(map(param => ({
            action: TEAM_EVENT_NAME.LEAVE_TEAM,
            team: param,
        }))), $removeTeamMember.pipe(withLatestFrom($myinfo), filter(([param, { account: myAccount }]) => param.accounts.includes(myAccount)), map(([param]) => ({
            action: TEAM_EVENT_NAME.LEAVE_TEAM,
            team: param.team,
        }))));
        const $contactJoinTeam = $addTeamMember.pipe(withLatestFrom($myinfo), filter(([param, { account: myAccount }]) => param.accounts.includes(myAccount)), map(([param]) => ({
            action: TEAM_EVENT_NAME.JOIN_TEAM,
            team: param.team,
        })));
        const $contactAddMember = $addTeamMember.pipe(withLatestFrom($myinfo), filter(([param, { account: myAccount }]) => !param.accounts.includes(myAccount)), map(([param]) => ({
            action: TEAM_EVENT_NAME.ADD_TEAM_MEMBERS,
            team: param.team,
            accounts: param.accounts,
        })));
        const $contactRemoveMember = $removeTeamMember.pipe(withLatestFrom($myinfo), filter(([param, { account: myAccount }]) => !param.accounts.includes(myAccount)), map(([param]) => ({
            action: TEAM_EVENT_NAME.REMOVE_TEAM_MEMBERS,
            team: param.team,
            accounts: param.accounts,
        })));
        const $contactTeamFull = merge($contactUpdateTeam, $contactAddMember, $contactJoinTeam, $contactRemoveMember, $contactTransferTeam).pipe(tap(args => {
            console.log('[im.team]contact.update', args);
        }), withLatestFrom(this.observable), mergeMap(([teamParams, teamMap]) => {
            const { team } = teamParams;
            const $requestTeam = defer(() => {
                const _request = this.sdk.excute('getTeam', {
                    teamId: team.teamId,
                });
                return from(_request);
            });
            return iif(() => lodashGet(teamMap, `${team.teamId}.teamId.length`, 0) !== 0, of(lodashGet(teamMap, `${team.teamId}`, {})), $requestTeam).pipe(map(team => (Object.assign(Object.assign({}, teamParams), { team: Object.assign(team, teamParams.team) }))));
        }));
        merge($contactTeamFull, $contactLeaveTeam)
            .pipe(tap(args => {
            console.log('[im.team]contactevent', args);
        }))
            .subscribe(param => {
            this.contactEventEmitter.emit(ImTeamStream.Contact_Eventnames.teamEvent, param);
        });
    }
    _onteams() {
        const onteams = (handler) => {
            this.sdk.subscrible('onteams', handler);
        };
        const offteams = (handler) => {
            this.sdk.unSubcrible('onteams', handler);
        };
        const $event = fromEventPattern(onteams, offteams);
        const $sessionReady = this.sdk.sessionStream.getSubject();
        const $pull = $sessionReady.pipe(map(sessions => sessions.length !== 0), filter(flag => flag), distinctUntilChanged(), switchMap(() => {
            var _a;
            const request = (_a = this.sdk) === null || _a === void 0 ? void 0 : _a.excute('getTeams', {});
            return from(request).pipe(timeout(10 * 1000), catchError(() => of().pipe(defaultIfEmpty([]))));
        }));
        return race($event, $pull).pipe(take(1));
    }
    _onupdateteam() {
        const onupdateTeam = (handler) => {
            this.sdk.subscrible('onUpdateTeam', handler);
        };
        const offupdateTeam = (handler) => {
            this.sdk.unSubcrible('onUpdateTeam', handler);
        };
        return fromEventPattern(onupdateTeam, offupdateTeam);
    }
    _onRemoveTeamMember() {
        const onAddTeamMembers = (handler) => {
            this.sdk.subscrible('onRemoveTeamMembers', handler);
        };
        const offAddTeamMembers = (handler) => {
            this.sdk.unSubcrible('onRemoveTeamMembers', handler);
        };
        return fromEventPattern(onAddTeamMembers, offAddTeamMembers);
    }
    _onAddTeamMember() {
        const onAddTeamMembers = (handler) => {
            this.sdk.subscrible('onAddTeamMembers', handler);
        };
        const offAddTeamMembers = (handler) => {
            this.sdk.unSubcrible('onAddTeamMembers', handler);
        };
        return fromEventPattern(onAddTeamMembers, offAddTeamMembers);
    }
    _onTransferTeam() {
        const onTransferTeam = (handler) => {
            this.sdk.subscrible('onTransferTeam', handler);
        };
        const offTransferTeam = (handler) => {
            this.sdk.unSubcrible('onTransferTeam', handler);
        };
        return fromEventPattern(onTransferTeam, offTransferTeam);
    }
    _onDissmissTeam() {
        const onDismissTeam = (handler) => {
            this.sdk.subscrible('onDismissTeam', handler);
        };
        const offDismissTeam = (handler) => {
            this.sdk.unSubcrible('onDismissTeam', handler);
        };
        return fromEventPattern(onDismissTeam, offDismissTeam);
    }
    getSubject() {
        return this.observable;
    }
    requestTeamById(teamId, _isForceUpdate = false) {
        this.eventEmitter.emit(ImTeamStream.Event_Names.request, {
            teamId,
            isForceUpdate: _isForceUpdate,
        });
    }
    getTeamById($teamId) {
        return this.observable.pipe(withLatestFrom($teamId), map(([teamMap, teamId]) => teamMap[teamId]));
    }
    getTeamField($props) {
        const $teamId = $props.pipe(map(([teamId]) => teamId));
        const $field = $props.pipe(map(([, field]) => field));
        return this.observable.pipe(withLatestFrom($teamId, $field), map(([teamMap, teamId, field]) => lodashGet(teamMap, `[${teamId}].${field}`, '')), distinctUntilChanged());
    }
    _excuteGetTeam(id) {
        return this.sdk.excute('getTeam', {
            teamId: id,
        });
    }
}
ImTeamStream.Event_Names = {
    request: 'request',
    onSyncTeams: 'onSyncTeams',
};
ImTeamStream.Contact_Eventnames = {
    teamEvent: 'IM_TEAM',
};
